import { useCallback } from 'react';
const CHUNK_SIZE = 10 * 1024 * 1024 * 5; // 50MB chunks

async function uploadVideoChunk(uploadUrl, chunk, startByte, endByte, totalSize) {
  const contentRange = "bytes ".concat(startByte, "-").concat(endByte - 1, "/").concat(totalSize);
  const formData = new FormData();
  formData.append('upload_url', uploadUrl);
  formData.append('chunk', chunk);
  const res = await fetch('/api/how_to_items/upload_video_chunk', {
    method: 'POST',
    headers: {
      'X-Content-Range': contentRange,
      'X-Chunk-Size': chunk.size.toString()
    },
    body: formData
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error("Video chunk upload failed: ".concat(error.error || res.status));
  }
  const result = await res.json();
  const gcsStatusCode = parseInt(result.status, 10);
  if (gcsStatusCode === 308) {
    return false;
  }
  if (gcsStatusCode === 200 || gcsStatusCode === 201) {
    return true;
  }
  throw new Error("Unexpected GCS status: ".concat(result.status));
}
export const getResumableVideoUploadUrl = async (filename, contentType) => {
  const response = await fetch('/api/how_to_items/presigned_url_for_video', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      file_name: filename,
      content_type: contentType
    })
  });
  if (!response.ok) {
    throw new Error('Failed to get presigned video URL');
  }
  const {
    upload_url,
    key
  } = await response.json();
  if (!upload_url || !key) throw new Error('Invalid resumable video upload URL');
  return {
    uploadUrl: upload_url,
    key
  };
};
export const uploadVideoFileToGCP = async (uppy, file, uploadUrl) => {
  const totalSize = file.size;
  let startByte = 0;
  while (startByte < totalSize) {
    const endByte = Math.min(startByte + CHUNK_SIZE, totalSize);
    const chunk = file.data.slice(startByte, endByte);
    const done = await uploadVideoChunk(uploadUrl, chunk, startByte, endByte, totalSize);
    const progress = Math.min(100, Math.round(endByte / totalSize * 100));
    uppy.emit('manual-progress-update', file, progress);
    if (done) {
      break;
    }
    startByte = endByte;
  }
  return true;
};
export const uploadVideoFile = async (uppy, file, onComplete) => {
  try {
    uppy.emit('manual-progress-update', file, 0);
    const {
      uploadUrl,
      key
    } = await getResumableVideoUploadUrl(file.name, file.type);
    file.meta.key = key;
    uppy.emit('manual-progress-update', file, 5);
    await uploadVideoFileToGCP(uppy, file, uploadUrl);
    uppy.emit('manual-progress-update', file, 100);
    const videoRecord = {
      name: file.name,
      key,
      content_type: file.type,
      size: file.size
    };
    uppy.emit('upload-success', file, {
      status: 200,
      body: videoRecord,
      videoRecord,
      uploadURL: uploadUrl
    });
    if (onComplete) {
      onComplete(videoRecord);
    }
  } catch (error) {
    console.error('Video upload failed:', error);
    uppy.emit('upload-error', file, error);
    throw error;
  }
};
const howToItemVideoUploadService = {
  useGetPresignedUrl: () => {
    return useCallback(async (fileName, contentType) => {
      return getResumableVideoUploadUrl(fileName, contentType);
    }, []);
  }
};
export default howToItemVideoUploadService;